import type { WritingFormat } from '../../lib/postTypes'
import { applyToggle, type MixEntry } from '../../lib/weightedMix'
import { WeightedMixEditor } from './WeightedMixEditor'
import { choiceCls, type SectionProps } from './types'

export function WritingSection({ draft, setDraft, formats }: SectionProps & { formats: WritingFormat[] }) {
  const mix: MixEntry[] = draft.writing.formatMix.map(m => ({ key: m.format, weight: m.weight }))
  const selectedKeys = mix.length > 0 ? mix.map(m => m.key) : [draft.writing.format]

  const writeMix = (format: string, entries: MixEntry[]) =>
    setDraft({
      ...draft,
      writing: { ...draft.writing, format, formatMix: entries.map(e => ({ format: e.key, weight: e.weight })) },
    })

  // 既存の重みは比率を保ったまま、増えた型に均等割り相当を渡して再配分する（weightedMix.rebalance）
  const toggleFormat = (key: string) => {
    const next = applyToggle(draft.writing.format, mix, key)
    if (next) writeMix(next.single, next.mix)   // null = 最後の1つは外させない
  }

  const labelOf = (key: string) => formats.find(f => f.key === key)?.label ?? key

  return (
    <div className="space-y-4">
      <div className="gm-card p-4 md:p-5">
        <p className="font-bold">X 投稿の型</p>
        <p className="mt-1 text-sm text-ink-muted">
          自動執筆で使う文章の構成パターンです。「おまかせ」はネタに合わせて AI が選びます。
          複数選ぶと、投稿ごとに指定した確率でランダムに使い分けます。
        </p>
        <div className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {formats.map(f => {
            const active = selectedKeys.includes(f.key)
            const weight = mix.find(m => m.key === f.key)?.weight
            return (
              <button key={f.key} data-testid={`format-${f.key}`} aria-pressed={active}
                onClick={() => toggleFormat(f.key)}
                className={`relative rounded-card border p-3 text-left transition-colors ${
                  active ? 'border-primary bg-primary-subtle ring-1 ring-primary' : 'border-line hover:border-line-strong'
                }`}>
                {weight != null && (
                  <span className="gm-num absolute right-2 top-2 rounded-full bg-primary px-2 py-0.5 text-xs font-bold text-[rgb(var(--gm-on-primary))]">
                    {weight}%
                  </span>
                )}
                <p className={`text-sm font-bold ${active ? 'text-primary' : ''}`}>{f.label}</p>
                <p className="mt-0.5 text-xs text-ink-muted">{f.description}</p>
                {f.example && (
                  <p className="mt-2 whitespace-pre-wrap rounded-ctl bg-surface-2 p-2 text-xs leading-relaxed text-ink-muted">
                    {f.example}
                  </p>
                )}
              </button>
            )
          })}
        </div>

        {mix.length > 0 && (
          <WeightedMixEditor title="型の配分" testId="format-mix-editor" entries={mix} labelOf={labelOf}
            onChange={entries => writeMix(draft.writing.format, entries)} />
        )}
      </div>

      <div className="gm-card p-4 md:p-5">
        <p className="font-bold">文字数の目安</p>
        <p className="mt-1 text-sm text-ink-muted">本文のおおよその長さです（X の上限 <span className="gm-num">140</span> 字の内側で調整します）。</p>
        <div className="mt-3 flex flex-wrap gap-2 text-sm" data-testid="target-length">
          {[0, 60, 80, 100, 120, 140].map(n => {
            const active = draft.writing.targetLength === n
            return (
              <button key={n} aria-pressed={active}
                onClick={() => setDraft({ ...draft, writing: { ...draft.writing, targetLength: n } })}
                className={choiceCls(active)}>
                {n === 0 ? 'おまかせ' : <>約<span className="gm-num">{n}</span>字</>}
              </button>
            )
          })}
        </div>
      </div>

      <div className="gm-card p-4 md:p-5">
        <label htmlFor="custom-prompt" className="block font-bold">カスタムプロンプト</label>
        <p className="mt-1 text-sm text-ink-muted">
          自動執筆への追加指示（500文字まで）。例: 「一人称は『店長』。季節の話題を一言入れる。絵文字は使わない」
        </p>
        <textarea id="custom-prompt" value={draft.writing.customPrompt} data-testid="custom-prompt"
          onChange={e => setDraft({ ...draft, writing: { ...draft.writing, customPrompt: e.target.value } })}
          rows={4} maxLength={500}
          className="gm-input mt-2 h-auto py-2"
          placeholder="（空欄なら追加指示なし）" />
        <p className="gm-num mt-1 text-right text-xs text-ink-muted">{draft.writing.customPrompt.length}/500</p>
      </div>
    </div>
  )
}
